/* @flow */
import * as React from "react";
import PropTypes from "prop-types";
import { withColorsProvider, type ColorSet } from "./ColorsProvider";
import NoStyleButton from "./NoStyleButton";
import prefixStyles from "./utils/prefixStyles";
import Theme from "./Theme";
import Tappable, { simpleActiveBackgroundColor } from "./Tappable";

const APP_BAR_PADDING = 14.25;
const APP_BAR_HEIGHT = 52;
const APP_BAR_TITLE_SIZE = 17.1;
const APP_BAR_TITLE_WEIGHT = 300;
const APP_BAR_HEIGHT_LARGE = 76;
const APP_BAR_TITLE_SIZE_LARGE = 22.8;

export const AppBarEmptyLeftIconPadding = () => (
  <span style={{ height: APP_BAR_HEIGHT, paddingRight: APP_BAR_PADDING }} />
);

type AppBarIconWrapperProps = {
  name?: ?string,
  children?: ?React.Node,
  onTap?: ?() => void,
  innerRef?: ?(?HTMLElement) => void,
  holdActiveUntil?: ?() => Promise<mixed>,
};

export class AppBarIconWrapper extends React.Component<AppBarIconWrapperProps> {
  static contextTypes = {
    isLargeAppLayout: PropTypes.bool,
  };

  render() {
    const {
      name,
      children,
      onTap,
      innerRef,
      holdActiveUntil,
      ...otherProps
    } = this.props;
    const isLargeAppLayout = this.context && this.context.isLargeAppLayout;

    return (
      <Tappable onTap={onTap} holdActiveUntil={holdActiveUntil}>
        {({ active, tapTargetRefHandler }) => {
          const styles = prefixStyles({
            button: {
              height: APP_BAR_HEIGHT,
              outline: "none",
              paddingLeft: APP_BAR_PADDING / 2,
              paddingRight: APP_BAR_PADDING / 2,
              position: "relative",
              ...(isLargeAppLayout ? { fontSize: "1.25em" } : null),
            },
            activeDisplay: {
              backgroundColor: simpleActiveBackgroundColor(active),
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: APP_BAR_PADDING / 2,
            },
            iconWrapper: {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            },
          });

          return (
            <NoStyleButton
              data-component-prop-name={name}
              style={styles.button}
              innerRef={(el) => {
                if (typeof innerRef === "function") {
                  innerRef(el);
                }
                tapTargetRefHandler(el);
              }}
              {...otherProps}
            >
              <div style={styles.activeDisplay}>
                <div style={styles.iconWrapper}>{children}</div>
              </div>
            </NoStyleButton>
          );
        }}
      </Tappable>
    );
  }
}

type AppBarButtonWrapperProps = {
  children?: ?React.Node,
};

export class AppBarButtonWrapper extends React.Component<AppBarButtonWrapperProps> {
  static contextTypes = {
    isLargeAppLayout: PropTypes.bool,
  };

  render() {
    const { children } = this.props;
    const isLargeAppLayout = this.context && this.context.isLargeAppLayout;

    const style = {
      fontSize: isLargeAppLayout ? "1.0em" : ".7em",
      marginLeft: Theme.Padding.APP_BAR_BUTTON,
      marginRight: Theme.Padding.APP_BAR_BUTTON,
    };
    return <span style={style}>{children}</span>;
  }
}

type AppBarProps = {
  title?: ?React.Node,
  iconLeft?: ?React.Node,
  iconRight?: ?React.Node,
  colors: ColorSet,
};

class AppBar extends React.Component<AppBarProps> {
  static contextTypes = {
    isLargeAppLayout: PropTypes.bool,
  };

  render() {
    const { title, iconLeft, iconRight, colors } = this.props;
    const isLargeAppLayout = this.context && this.context.isLargeAppLayout;

    const leftSafePadding = isLargeAppLayout
      ? "0px"
      : Theme.Padding.SAFE_AREA_LEFT;
    const styles = prefixStyles({
      container: {
        backgroundColor: colors.PRIMARY,
        backgroundImage: `linear-gradient(35deg, ${colors.PRIMARY_GRADIENT_DARK}, ${colors.PRIMARY_GRADIENT_LIGHT})`,
        color: colors.PRIMARY_TEXT,
        paddingTop: Theme.Padding.SAFE_AREA_TOP,
        height: `calc(${
          isLargeAppLayout ? APP_BAR_HEIGHT_LARGE : APP_BAR_HEIGHT
        }px + ${Theme.Padding.SAFE_AREA_TOP})`,
        marginLeft: `calc(-1 * ${leftSafePadding})`,
        paddingLeft: leftSafePadding,
        marginRight: `calc(-1 * ${Theme.Padding.SAFE_AREA_RIGHT})`,
        paddingRight: Theme.Padding.SAFE_AREA_RIGHT,
        display: "flex",
        alignItems: "center",
        userSelect: "none",
      },
      title: {
        flex: "1 1 auto",
        fontWeight: APP_BAR_TITLE_WEIGHT,
        fontSize: isLargeAppLayout
          ? APP_BAR_TITLE_SIZE_LARGE
          : APP_BAR_TITLE_SIZE,
        paddingLeft: iconLeft ? 0 : APP_BAR_PADDING,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: "normal",
      },
      icon: {
        flex: "0 0 auto",
        fontSize: 19,
        color: colors.SECONDARY,
      },
    });

    return (
      <div data-component-name="AppBar" style={styles.container}>
        {iconLeft ? (
          <span style={styles.icon}>{iconLeft}</span>
        ) : (
          <AppBarEmptyLeftIconPadding />
        )}
        <span style={styles.title}>{title}</span>
        {iconRight && <span style={styles.icon}>{iconRight}</span>}
      </div>
    );
  }
}

export default withColorsProvider(AppBar);



// WEBPACK FOOTER //
// ../nx-components/src/AppBar.jsx